import React, { useState } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { SearchIcon, FilterIcon, MapPinIcon, MapIcon, TableIcon, FolderIcon, ChevronLeftIcon, ChevronRightIcon, ChevronsUpDownIcon, PlusIcon, SettingsIcon, XIcon, PaperclipIcon, CheckSquareIcon, SquareIcon, LinkIcon, TagIcon, FileTextIcon, PrinterIcon, HardHatIcon, FileCheckIcon, CreditCardIcon, AlertCircleIcon, CheckCircle2Icon, ClockIcon } from 'lucide-react';
type ViewMode = 'list' | 'map';
type Status = 'Nouvelle demande' | 'En analyse' | 'En attente de documents' | 'Approuvé' | 'Refusé' | 'Émis';
interface Permit {
  id: string;
  type: string;
  category: string;
  permitId: string;
  location: string;
  date: string;
  status: Status;
  value: string;
  requester: {
    name: string;
    initials: string;
    color: string;
    phone: string;
    email: string;
    type: string;
  };
  contractor?: {
    name: string;
    rbq: string;
  };
}
const MOCK_PERMITS: Permit[] = [{
  id: '1',
  type: 'Construction neuve - Résidentiel',
  category: 'Construction',
  permitId: 'P-2026-142',
  location: '152, rue Saint-Georges, Windsor',
  date: '8 juin 2026',
  status: 'En analyse',
  value: '450 000 $',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]',
    phone: '(613) 446-1157',
    email: 'nouellet@email.com',
    type: 'Propriétaire'
  },
  contractor: {
    name: 'Constructions Tremblay inc.',
    rbq: '8321-4567-89'
  }
}, {
  id: '2',
  type: 'Agrandissement principal',
  category: 'Rénovation',
  permitId: 'P-2026-141',
  location: '205, rue Ambroise-Dearden, Windsor',
  date: '7 juin 2026',
  status: 'En attente de documents',
  value: '85 000 $',
  requester: {
    name: 'Marc Lavoie',
    initials: 'ML',
    color: 'bg-emerald-500',
    phone: '(613) 222-3344',
    email: 'mlavoie@email.com',
    type: 'Propriétaire'
  }
}, {
  id: '3',
  type: "Abattage d'arbre",
  category: 'Aménagement paysager',
  permitId: 'P-2026-140',
  location: '290, rue Georges-Guilbault, Windsor',
  date: '6 juin 2026',
  status: 'Émis',
  value: '1 200 $',
  requester: {
    name: 'Caroline Nadeau',
    initials: 'CN',
    color: 'bg-amber-500',
    phone: '(613) 555-6677',
    email: 'cnadeau@email.com',
    type: 'Locataire'
  },
  contractor: {
    name: 'Arboriculture Pro',
    rbq: 'N/A'
  }
}, {
  id: '4',
  type: 'Installation de piscine creusée',
  category: 'Aménagement extérieur',
  permitId: 'P-2026-139',
  location: '32, 3e Avenue, Windsor',
  date: '5 juin 2026',
  status: 'Approuvé',
  value: '45 000 $',
  requester: {
    name: 'Jean Dubois',
    initials: 'JD',
    color: 'bg-blue-500',
    phone: '(613) 999-8877',
    email: 'jdubois@email.com',
    type: 'Propriétaire'
  }
}, {
  id: '5',
  type: 'Rénovation de façade',
  category: 'Rénovation',
  permitId: 'P-2026-138',
  location: '70, rue Principale Nord, Windsor',
  date: '4 juin 2026',
  status: 'Nouvelle demande',
  value: '25 000 $',
  requester: {
    name: 'Sophie Tremblay',
    initials: 'ST',
    color: 'bg-purple-500',
    phone: '(613) 111-2233',
    email: 'stremblay@email.com',
    type: 'Propriétaire'
  }
}];
const getStatusColor = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-200 text-slate-700';
    case 'En analyse':
      return 'bg-indigo-100 text-indigo-700';
    case 'En attente de documents':
      return 'bg-amber-100 text-amber-700';
    case 'Approuvé':
      return 'bg-emerald-100 text-emerald-700';
    case 'Émis':
      return 'bg-blue-100 text-blue-700';
    case 'Refusé':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};
const getStatusDot = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-400';
    case 'En analyse':
      return 'bg-[#4F46E5]';
    case 'En attente de documents':
      return 'bg-amber-500';
    case 'Approuvé':
      return 'bg-emerald-500';
    case 'Émis':
      return 'bg-blue-500';
    case 'Refusé':
      return 'bg-red-500';
    default:
      return 'bg-slate-400';
  }
};
// Request locations around Windsor, QC
const MAP_MARKERS: {
  permitId: string;
  type: string;
  category: string;
  position: [number, number];
}[] = [{
  permitId: 'P-2026-142',
  type: 'Construction neuve',
  category: 'Construction',
  position: [45.4528, -73.4622]
}, {
  permitId: 'P-2026-141',
  type: 'Agrandissement',
  category: 'Rénovation',
  position: [45.4468, -73.4688]
}, {
  permitId: 'P-2026-140',
  type: "Abattage d'arbre",
  category: 'Aménagement',
  position: [45.4552, -73.4578]
}];
// Custom indigo map marker
const permitMarkerIcon = L.divIcon({
  className: '',
  html: `<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:9999px;background:#4F46E5;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid white;"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><path d="m9 15 2 2 4-4"/></svg></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14]
});
type RightTab = 'gestion' | 'collaboration' | 'historique';
export function PermisGerer() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedPermit, setSelectedPermit] = useState<Permit | null>(null);
  const [rightTab, setRightTab] = useState<RightTab>('gestion');
  if (selectedPermit) {
    return <div className="bg-slate-50 -mx-4 sm:-mx-6 lg:-mx-8 -my-8 min-h-screen flex" data-id="element-4398" data-mp="vejn777q25pv">
        {/* Left Pane - Citizen Visible */}
        <div className="w-1/2 border-r border-slate-200 bg-white flex flex-col" data-id="element-4399" data-mp="10btl7b96fppb">
          <div className="p-4 border-b border-slate-200 flex items-center gap-2 text-slate-500 text-sm font-medium" data-id="element-4400" data-mp="15by4ta5dt0jr">
            <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex items-center justify-center" data-id="element-4401" data-mp="1a9zo9w6pz1vr">
              <div className="w-1.5 h-1.5 bg-slate-300 rounded-full" data-id="element-4402" data-mp="cp3n2kzy8f9z"></div>
            </div>
            Visible par le citoyen (Portail)
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6" data-id="element-4403" data-mp="ml6mvk7bw1tr">
            <button className="pb-3 border-b-2 border-[#4F46E5] text-[#4F46E5] font-semibold text-sm" data-id="element-4404" data-mp="mjvm7t96ja6v">
              Infos
            </button>
            <button className="pb-3 border-b-2 border-transparent text-slate-500 hover:text-slate-700 font-semibold text-sm" data-id="element-4405" data-mp="3q1drbsxki33l">
              Communications
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1" data-id="element-4406" data-mp="2c6owbh8zap6c">
            <div className="flex justify-between items-start mb-8" data-id="element-4407" data-mp="2c42pxkxtqfpp">
              <div className="flex gap-4" data-id="element-4408" data-mp="285bpp4y2ahyd">
                <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-4409" data-mp="235qsayyjihnr">
                  <FileCheckIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-4410" data-mp="1y5ukeomq262x" />
                </div>
                <div data-id="element-4411" data-mp="1t8ky3humqajb">
                  <div className="flex items-center gap-2" data-id="element-4412" data-mp="1t944q2etxci1">
                    <h2 className="text-xl font-bold text-slate-900" data-id="element-4413" data-mp="1ogb3vpmheob8">
                      {selectedPermit.type}
                    </h2>
                    <ChevronLeftIcon className="w-4 h-4 text-slate-400 rotate-[-90deg]" data-id="element-4414" data-mp="1kfr7io9qfoq5" />
                  </div>
                  <div className="text-sm text-slate-500 mt-1 flex items-center gap-2" data-id="element-4415" data-mp="1fe1rsvbkemvq">
                    <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium border border-slate-200 text-slate-700" data-id="element-4416" data-mp="1ag9zi070cyr0">
                      {selectedPermit.permitId}
                    </span>
                    <span data-id="element-4417" data-mp="2ucxq28u33t4q">{selectedPermit.category}</span>
                  </div>
                  <div className="text-sm text-slate-400 mt-1 ml-10 border-l-2 border-slate-200 pl-2" data-id="element-4418" data-mp="2yd7rr9aufxds">
                    Déposée le {selectedPermit.date}
                  </div>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(selectedPermit.status)}`} data-id="element-4419" data-mp="2pf5xdclm82zh">
                <div className={`w-2 h-2 rounded-full ${getStatusDot(selectedPermit.status)}`} data-id="element-4420" data-mp="2ffos2wa9zr41"></div>
                {selectedPermit.status}
              </div>
            </div>

            <div className="mb-8" data-id="element-4421" data-mp="26mtkhvxc5jb3">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-4422" data-mp="1rpb0t7zilwai">
                DESCRIPTION DES TRAVAUX
              </h3>
              <div className="p-4 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm leading-relaxed" data-id="element-4423" data-mp="1wnvzv43169xd">
                Construction d'une maison unifamiliale isolée de 2 étages avec
                garage intégré. Revêtement extérieur en brique et déclin de
                bois. Aménagement paysager complet incluant l'entrée d'auto en
                pavé uni.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-8" data-id="element-4424" data-mp="1i3ruf52u58e6">
              <div data-id="element-4425" data-mp="1w0erd7usfyk3">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-4426" data-mp="1vzwxuizifyfv">
                  REQUÉRANT
                </h3>
                <div className="flex items-start gap-3" data-id="element-4427" data-mp="25r9nr51r0ej9">
                  <div className={`w-8 h-8 rounded-full ${selectedPermit.requester.color} text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5`} data-id="element-4428" data-mp="25qrnlxbytg62">
                    {selectedPermit.requester.initials}
                  </div>
                  <div data-id="element-4429" data-mp="2arxfjsxswzej">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-4430" data-mp="2ao9od0ftwp2z">
                      {selectedPermit.requester.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 bg-slate-100 inline-block px-1.5 py-0.5 rounded" data-id="element-4431" data-mp="2nvbzf0lggskh">
                      {selectedPermit.requester.type}
                    </div>
                    <div className="text-sm text-slate-500 mt-1.5" data-id="element-4432" data-mp="2ewqa8mlhafaq">
                      {selectedPermit.requester.phone}
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-4433" data-mp="3bjli3tndh07e">
                      {selectedPermit.requester.email}
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-4434" data-mp="32n4149sbu83q">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-4435" data-mp="2xmpu8v2gp47a">
                  EMPLACEMENT & VALEUR
                </h3>
                <div className="flex items-start gap-2 mb-3" data-id="element-4436" data-mp="1r7fi1ti71t87">
                  <MapPinIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-4437" data-mp="1m5zvfx0dbenp" />
                  <div data-id="element-4438" data-mp="1ps0ciwgt7o3">
                    <div className="text-sm font-medium text-slate-900" data-id="element-4439" data-mp="1qa37ydmuk4z">
                      {selectedPermit.location.split(',')[0]}
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-4440" data-mp="apx5eme6rtz3">Windsor, QC</div>
                    <button className="text-xs text-[#4F46E5] mt-1 font-medium hover:underline" data-id="element-4441" data-mp="anu7lg6skwjt">
                      Voir la sur la carte
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm" data-id="element-4442" data-mp="peb8myfilkg6">
                  <span className="text-slate-500" data-id="element-4443" data-mp="pcqgtgkl4fm0">Valeur estimée:</span>
                  <span className="font-semibold text-slate-900" data-id="element-4444" data-mp="uezayg971npp">
                    {selectedPermit.value}
                  </span>
                </div>
              </div>
            </div>

            {selectedPermit.contractor && <div className="mb-8 p-4 rounded-xl bg-slate-50 border border-slate-200" data-id="element-4445" data-mp="2w7doz48ma80u">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-4446" data-mp="308p5mk9ijiww">
                  <HardHatIcon className="w-4 h-4" data-id="element-4447" data-mp="2d1afouiwgwp" /> ENTREPRENEUR
                </h3>
                <div className="flex justify-between items-center" data-id="element-4448" data-mp="2fe0yqecjzld">
                  <div data-id="element-4449" data-mp="gedply1v0ehj">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-4450" data-mp="gapvawolbo1r">
                      {selectedPermit.contractor.name}
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5" data-id="element-4451" data-mp="q9yldegc4iiu">
                      Licence RBQ: {selectedPermit.contractor.rbq}
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded border border-emerald-200 flex items-center gap-1" data-id="element-4452" data-mp="101bgcexfjot3">
                    <CheckCircle2Icon className="w-3 h-3" data-id="element-4453" data-mp="v3tc31ucxxrf" /> RBQ Valide
                  </span>
                </div>
              </div>}

            <div data-id="element-4454" data-mp="1436exbnz7o36">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-4455" data-mp="143ptcief3ucg">
                DOCUMENTS JUSTIFICATIFS SOUMIS
              </h3>
              <div className="space-y-3" data-id="element-4456" data-mp="1sn1gfgsdrcok">
                <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white hover:border-[#4F46E5] transition-colors cursor-pointer group" data-id="element-4457" data-mp="1smrmji47sldl">
                  <div className="flex items-center gap-3" data-id="element-4458" data-mp="1io2f6j9y2jbl">
                    <div className="w-10 h-10 rounded bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-4459" data-mp="14xp8ek8shanu">
                      <FileTextIcon className="w-5 h-5" data-id="element-4460" data-mp="19oxgncydupzf" />
                    </div>
                    <div data-id="element-4461" data-mp="uzj7o5ypyakk">
                      <div className="text-sm font-semibold text-slate-900 group-hover:text-[#4F46E5] transition-colors" data-id="element-4462" data-mp="v0ar6if1khuh">
                        Plans d'architecture complets
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-4463" data-mp="lyl05dp2nkx8">
                        PDF • 4.2 MB • Requis
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-4464" data-mp="ixrmba0xawk2">
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded" data-id="element-4465" data-mp="izcewu3og5fa">
                      Conforme
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white hover:border-[#4F46E5] transition-colors cursor-pointer group" data-id="element-4466" data-mp="952dkmwrbwsf">
                  <div className="flex items-center gap-3" data-id="element-4467" data-mp="81tutkmumvb">
                    <div className="w-10 h-10 rounded bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-4468" data-mp="3rcs152b5k8pu">
                      <FileTextIcon className="w-5 h-5" data-id="element-4469" data-mp="3meqbtzn4vd5x" />
                    </div>
                    <div data-id="element-4470" data-mp="3id4y6c2ryuuo">
                      <div className="text-sm font-semibold text-slate-900 group-hover:text-[#4F46E5] transition-colors" data-id="element-4471" data-mp="3idy111ym507j">
                        Certificat de localisation
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-4472" data-mp="1gliaaje6jg2f">
                        PDF • 1.1 MB • Requis
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-4473" data-mp="33gf06ouf9hz5">
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded" data-id="element-4474" data-mp="388ooy1jswbzu">
                      Conforme
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border border-red-200 bg-red-50 cursor-pointer group" data-id="element-4475" data-mp="2fkarbmwmjhot">
                  <div className="flex items-center gap-3" data-id="element-4476" data-mp="2kjdrou1nszqg">
                    <div className="w-10 h-10 rounded bg-red-100 flex items-center justify-center text-red-600" data-id="element-4477" data-mp="2ojxo1veerzbj">
                      <AlertCircleIcon className="w-5 h-5" data-id="element-4478" data-mp="2tkleaeutcc4p" />
                    </div>
                    <div data-id="element-4479" data-mp="3uzx071296yk">
                      <div className="text-sm font-semibold text-red-900" data-id="element-4480" data-mp="3tf56p64s24e">
                        Soumission détaillée de l'entrepreneur
                      </div>
                      <div className="text-xs text-red-700" data-id="element-4481" data-mp="24vwoj3yyyfnv">
                        Manquant • Requis
                      </div>
                    </div>
                  </div>
                  <button className="text-xs font-medium text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded transition-colors" data-id="element-4482" data-mp="2eulvw2t8ohpv">
                    Demander au citoyen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Pane - Internal */}
        <div className="w-1/2 bg-slate-50 flex flex-col relative" data-id="element-4483" data-mp="1h7mz7s2jr9rz">
          <button onClick={() => setSelectedPermit(null)} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200 transition-colors z-10" data-id="element-4484" data-mp="1vvrhiuxotja8">
            <XIcon className="w-5 h-5" data-id="element-4485" data-mp="13ex85985veo0" />
          </button>

          <div className="p-4 border-b border-slate-200 flex items-center justify-between text-slate-500 text-sm font-medium bg-white" data-id="element-4486" data-mp="13ddwxx1d3tt3">
            <div className="flex items-center gap-2" data-id="element-4487" data-mp="3a37dotp90kg6">
              <div className="w-4 h-4 rounded flex items-center justify-center border-2 border-slate-300" data-id="element-4488" data-mp="3a1mir2z0uzhs">
                <div className="w-2 h-2 bg-slate-300 rounded-sm" data-id="element-4489" data-mp="3ewab5cx5ce1n"></div>
              </div>
              Interne — Traitement de la demande
            </div>
            <PrinterIcon className="w-4 h-4 mr-10" data-id="element-4490" data-mp="355ny9qgte1mn" />
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6 bg-white" data-id="element-4491" data-mp="3sugrvg9yrxe3">
            <button onClick={() => setRightTab('gestion')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${rightTab === 'gestion' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-4492" data-mp="1nxf3vkkhxd5">
              Gestion
            </button>
            <button onClick={() => setRightTab('collaboration')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${rightTab === 'collaboration' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-4493" data-mp="28dc2b3c500jm">
              Collaboration
            </button>
            <button onClick={() => setRightTab('historique')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${rightTab === 'historique' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-4494" data-mp="304b0jlgad2hn">
              Historique
            </button>
          </div>

          {rightTab === 'gestion' && <div className="p-6 overflow-y-auto flex-1 space-y-8" data-id="element-4495" data-mp="3iushytvo4bnp">
              {/* Workflow — Horizontal Stepper Chronology */}
              <div data-id="element-4496" data-mp="3934rav6xqyna">
                <div className="flex items-center justify-between mb-4" data-id="element-4497" data-mp="391tr15cp890x">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-4498" data-mp="ad4pvrbygkf8">
                    PROCESSUS
                  </h3>
                  <span className="text-xs font-medium text-slate-500 bg-white px-2 py-1 rounded border border-slate-200" data-id="element-4499" data-mp="5klaqpxmdh8b">
                    Délai légal: 30 jours
                  </span>
                </div>

                <div className="flex items-start" data-id="element-4500" data-mp="hy89o4y3wsu4">
                  {[{
                label: 'Réception',
                sub: 'Vérification préliminaire',
                meta: '8 juin 2026',
                state: 'done'
              }, {
                label: 'Analyse réglementaire',
                sub: 'Zonage · marges · hauteur',
                meta: 'En cours',
                state: 'current'
              }, {
                label: 'Approbation PIIA',
                sub: 'Comité (CCU)',
                meta: 'À venir',
                state: 'upcoming'
              }, {
                label: 'Émission',
                sub: 'Paiement · délivrance',
                meta: 'À venir',
                state: 'upcoming'
              }].map((step, i, arr) => {
                const isLast = i === arr.length - 1;
                const dotClass = step.state === 'done' ? 'bg-emerald-500 text-white border-emerald-500' : step.state === 'current' ? 'bg-[#4F46E5] text-white border-[#4F46E5] ring-4 ring-indigo-50' : 'bg-white text-slate-400 border-slate-300';
                return <div key={step.label} className="flex-1 flex flex-col items-center text-center" data-id="element-4501" data-mp="3brlcg9z8toar" data-mpi={i}>
                        <div className="flex items-center w-full" data-id="element-4502" data-mp="3krq9praq36b7">
                          <div className="flex-1 h-0.5" data-id="element-4503" data-mp="3kp45o1n1j8xq">
                            {i !== 0 && <div className={`h-0.5 ${step.state === 'done' ? 'bg-emerald-500' : 'bg-slate-200'}`} data-id="element-4504" data-mp="1e2ol1vh85v9n"></div>}
                          </div>
                          <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${dotClass}`} data-id="element-4505" data-mp="1531lzgbyx1jr">
                            {step.state === 'done' ? <CheckCircle2Icon className="w-4 h-4" data-id="element-4506" data-mp="1wv0lgcobimh5" /> : step.state === 'current' ? <ClockIcon className="w-4 h-4" data-id="element-4507" data-mp="1o0ck2xlv5oxm" /> : <span className="text-xs font-bold" data-id="element-4508" data-mp="1j2b08agm5ll3">{i + 1}</span>}
                          </div>
                          <div className="flex-1 h-0.5" data-id="element-4509" data-mp="2giq9c8t4w2l3">
                            {!isLast && <div className={`h-0.5 ${arr[i + 1].state === 'done' ? 'bg-emerald-500' : 'bg-slate-200'}`} data-id="element-4510" data-mp="2bp5gswab80ta"></div>}
                          </div>
                        </div>
                        <div className="mt-2 px-1" data-id="element-4511" data-mp="2ltlv3l8559uz">
                          <div className={`text-xs font-semibold leading-tight ${step.state === 'upcoming' ? 'text-slate-500' : 'text-slate-900'}`} data-id="element-4512" data-mp="2pvz027f2d3qp">
                            {step.label}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5 leading-tight" data-id="element-4513" data-mp="2gt7ng4agc1xh">
                            {step.sub}
                          </div>
                          <div className={`text-[10px] mt-1 font-medium ${step.state === 'current' ? 'text-[#4F46E5]' : 'text-slate-400'}`} data-id="element-4514" data-mp="26y609dzql9xj">
                            {step.meta}
                          </div>
                        </div>
                      </div>;
              })}
                </div>

                {/* Current step regulatory analysis detail */}
                <div className="mt-6 bg-white p-4 rounded-xl border border-slate-200 shadow-sm" data-id="element-4515" data-mp="1lkdupr1vr3ym">
                  <div className="flex items-center justify-between mb-3" data-id="element-4516" data-mp="1zfg3cly437f4">
                    <div className="flex items-center gap-2" data-id="element-4517" data-mp="1ulkd65o3aoui">
                      <div className="w-6 h-6 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-4518" data-mp="29azxp3x11y1r">
                        JD
                      </div>
                      <span className="text-xs font-medium text-slate-700" data-id="element-4519" data-mp="2icpt0ku15h4t">
                        Analyse réglementaire — Jacques Dubois (Urbanisme)
                      </span>
                    </div>
                    <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded" data-id="element-4520" data-mp="nzhr697n059o">
                      En cours
                    </span>
                  </div>
                  <div className="space-y-2" data-id="element-4521" data-mp="les4wjpgnoek">
                    <label className="flex items-center gap-2 text-sm text-slate-600" data-id="element-4522" data-mp="ldqpy45bqhrk">
                      <input type="checkbox" className="rounded text-[#4F46E5] focus:ring-[#4F46E5]" checked readOnly data-id="element-4523" data-mp="pe11dwbvy0dt" />
                      Zonage résidentiel H-123 conforme
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-600" data-id="element-4524" data-mp="1o6ml980a1qx">
                      <input type="checkbox" className="rounded text-[#4F46E5] focus:ring-[#4F46E5]" checked readOnly data-id="element-4525" data-mp="6pdy9jd9rl2v" />
                      Marges de recul conformes
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-900 font-medium" data-id="element-4526" data-mp="3f338xaq55nh0">
                      <input type="checkbox" className="rounded text-[#4F46E5] focus:ring-[#4F46E5]" data-id="element-4527" data-mp="3k1o0klrbuytu" />
                      Hauteur maximale respectée
                    </label>
                  </div>
                </div>
              </div>

              {/* Conditions & Exigences */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-4528" data-mp="2xmfipgrsuxz4">
                <div className="flex items-center justify-between mb-4" data-id="element-4529" data-mp="2tlvmcff1vye1">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-4530" data-mp="2ov6wt5mxlbhj">
                    CONDITIONS À JOINDRE AU PERMIS
                  </h3>
                  <button className="text-xs font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-4531" data-mp="2jw3tbpmlndbo">
                    + Ajouter
                  </button>
                </div>
                <div className="space-y-2" data-id="element-4532" data-mp="29wcvbdytyg6u">
                  <div className="flex items-start gap-2 p-2 bg-amber-50 rounded border border-amber-100" data-id="element-4533" data-mp="29zh9n460esb8">
                    <AlertCircleIcon className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-4534" data-mp="1vaxnnjku8eyn" />
                    <p className="text-sm text-amber-900" data-id="element-4535" data-mp="20a0rx1szuh5k">
                      L'entrepreneur doit s'assurer de ne pas obstruer la voie
                      publique pendant les travaux.
                    </p>
                  </div>
                  <div className="flex items-start gap-2 p-2 bg-slate-50 rounded border border-slate-200" data-id="element-4536" data-mp="1radpqdsfx7bg">
                    <AlertCircleIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-4537" data-mp="2f081st4xqrun" />
                    <p className="text-sm text-slate-600" data-id="element-4538" data-mp="2izgvjxzz6pq7">
                      Une inspection des fondations est requise avant le
                      remblayage.
                    </p>
                  </div>
                </div>
              </div>

              {/* Internal Files */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-4539" data-mp="2xxj3eke1hxd9">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-4540" data-mp="32p11n0oohjuj">
                  DOCUMENTS INTERNES (NON VISIBLES AU CITOYEN)
                </h3>
                <div className="flex gap-3" data-id="element-4541" data-mp="381pntanpgf3z">
                  <div className="w-24 rounded-lg border border-slate-200 p-3 flex flex-col items-center justify-center text-center hover:border-[#4F46E5] cursor-pointer transition-colors group" data-id="element-4542" data-mp="380o8gtznp6gg">
                    <FileTextIcon className="w-8 h-8 text-indigo-300 group-hover:text-[#4F46E5] mb-2 transition-colors" data-id="element-4543" data-mp="3gv2pb0zu4p1o" />
                    <div className="text-[10px] font-medium text-slate-700 w-full leading-tight" data-id="element-4544" data-mp="2t4yu9i0ppxma">
                      Grille d'analyse réglementaire
                    </div>
                  </div>
                  <button className="w-24 h-28 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:text-[#4F46E5] hover:border-[#4F46E5] transition-colors" data-id="element-4545" data-mp="333xwoiuvwx0u">
                    <PlusIcon className="w-5 h-5 mb-1" data-id="element-4546" data-mp="ie8jea9txja9" />
                    <span className="text-xs font-medium" data-id="element-4547" data-mp="dc9hl4tqmuff">Générer</span>
                  </button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-200" data-id="element-4548" data-mp="2bumoh0a8284a">
                <button className="flex-1 bg-[#0F172A] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-slate-800 transition-colors" data-id="element-4549" data-mp="2gu7n19lhcegh">
                  Approuver l'étape actuelle
                </button>
                <button className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors" data-id="element-4550" data-mp="2qkamnsn3wqns">
                  Mettre en attente
                </button>
              </div>
            </div>}

          {rightTab === 'collaboration' && <div className="p-6 overflow-y-auto flex-1 space-y-6" data-id="element-4551" data-mp="34iquxo0map81">
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-4552" data-mp="156pjvw5z08ij">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-4553" data-mp="15927opyetd3i">
                  INTERVENANTS ASSIGNÉS
                </h3>
                <div className="space-y-3" data-id="element-4554" data-mp="12nb6gkw3jple">
                  <div className="flex items-center justify-between" data-id="element-4555" data-mp="12o2w7f30it3r">
                    <div className="flex items-center gap-3" data-id="element-4556" data-mp="xtyouupu4j25">
                      <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-4557" data-mp="svoqkb8td2cb">
                        JD
                      </div>
                      <div data-id="element-4558" data-mp="1llnj4gm7vhlq">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-4559" data-mp="1ljt0j8yf41kt">
                          Jacques Dubois
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-4560" data-mp="1bmy3kuhsm98n">
                          Urbanisme · Responsable
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-100" data-id="element-4561" data-mp="7swzfejjqjr">
                      Actif
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-4562" data-mp="2r2oev8uphl5h">
                    <div className="flex items-center gap-3" data-id="element-4563" data-mp="2r0lhu4v4yrr9">
                      <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-4564" data-mp="2m563zcx4ovuu">
                        ST
                      </div>
                      <div data-id="element-4565" data-mp="2w196wkzt0yiu">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-4566" data-mp="2vzy2qk2g5or7">
                          Sylvie Tremblay
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-4567" data-mp="35slwt721lem8">
                          Réception · Collaboratrice
                        </div>
                      </div>
                    </div>
                    <button className="text-xs font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-4568" data-mp="3es92i51t16l3">
                      Voir
                    </button>
                  </div>
                </div>
                <button className="mt-4 flex items-center gap-2 text-sm font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-4569" data-mp="1kfavbubeiq1z">
                  <PlusIcon className="w-4 h-4" data-id="element-4570" data-mp="3w35ztp7ns46v" /> Ajouter un intervenant
                </button>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-4571" data-mp="4urzsjjipzyo">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-4572" data-mp="3mb9upu75ndt5">
                  NOTES INTERNES
                </h3>
                <div className="space-y-3" data-id="element-4573" data-mp="3dbd6tuslaiz5">
                  <div className="flex gap-3" data-id="element-4574" data-mp="3dak6bbk84ppg">
                    <div className="w-7 h-7 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" data-id="element-4575" data-mp="3iagbhe4nqn6l">
                      JD
                    </div>
                    <div className="flex-1" data-id="element-4576" data-mp="38ca62mjn6ywz">
                      <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 border border-slate-200" data-id="element-4577" data-mp="38d1vffmnc0et">
                        Marges de recul validées sur le certificat de
                        localisation. Reste à confirmer la hauteur au faîte.
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1" data-id="element-4578" data-mp="154rod3h1r1ba">
                        Jacques Dubois · il y a 2 h
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2" data-id="element-4579" data-mp="v62g82eycv88">
                  <input type="text" placeholder="Ajouter une note interne..." className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-4580" data-mp="r4qqlww2wa01" />
                  <button className="px-4 py-2 bg-[#0F172A] text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors" data-id="element-4581" data-mp="1e3d5qvas8aah">
                    Publier
                  </button>
                </div>
              </div>
            </div>}

          {rightTab === 'historique' && <div className="p-6 overflow-y-auto flex-1" data-id="element-4582" data-mp="21t7i7br6vwu7">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-4583" data-mp="3kh0k9sde3o2o">
                JOURNAL D'ACTIVITÉ
              </h3>
              <div className="relative border-l-2 border-slate-200 ml-3 space-y-6" data-id="element-4584" data-mp="3pgditadmijl3">
                <div className="relative pl-6" data-id="element-4585" data-mp="3tgdzz2s3x8vu">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#4F46E5] border-2 border-white" data-id="element-4586" data-mp="3tjrx9wlwyrwf"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-4587" data-mp="7aqr3vn2av0b">
                    Analyse réglementaire débutée
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-4588" data-mp="c7h6shuyfol7">
                    Jacques Dubois (Urbanisme)
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-4589" data-mp="l1m0ra5dxg4y">
                    8 juin 2026 · 14:32
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-4590" data-mp="q29tq1d6c71t">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white" data-id="element-4591" data-mp="uzjrq5empr19"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-4592" data-mp="hcb1b6lqn2ys">
                    Documents vérifiés et conformes
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-4593" data-mp="cbwxk0r66f6k">
                    Sylvie Tremblay (Réception)
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-4594" data-mp="m39j6amdk945">
                    8 juin 2026 · 10:15
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-4595" data-mp="3ujurdp76my67">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-slate-300 border-2 border-white" data-id="element-4596" data-mp="3pl1ika2ubvcd"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-4597" data-mp="8d5yvp722phc">
                    Demande de permis déposée
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-4598" data-mp="3fny4m36zgkf">
                    Nathalie Ouellet (Portail citoyen)
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-4599" data-mp="1erf3iw9inw3g">
                    8 juin 2026 · 09:02
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>;
  }
  return <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-4600" data-mp="1r8zmcz4eo3a7">
      {/* Header */}
      <div className="p-4 border-b border-slate-200" data-id="element-4601" data-mp="1hcwkludgu6ns">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" data-id="element-4602" data-mp="1hdo6gdh9gq0v">
          <div className="flex items-center gap-3" data-id="element-4603" data-mp="253s9aimmklqt">
            <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 gap-1" data-id="element-4604" data-mp="2a1k9dzxfbe63">
              <button onClick={() => setViewMode('list')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4605" data-mp="2a3elcoqpvvy1">
                <TableIcon className="w-4 h-4" data-id="element-4606" data-mp="2ss1pn512khak" />
                Tableau
              </button>
              <button onClick={() => setViewMode('map')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'map' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4607" data-mp="32o1uxjs9bje4">
                <MapIcon className="w-4 h-4" data-id="element-4608" data-mp="2ooj0ftsdc6ka" />
                Carte
              </button>
            </div>
            <span className="text-sm text-slate-500" data-id="element-4609" data-mp="2jpplry1ci9ij">
              142 demandes de permis
            </span>
          </div>

          <div className="flex items-center gap-3" data-id="element-4610" data-mp="2exekaqqyifnv">
            <div className="relative" data-id="element-4611" data-mp="2awvqws9f11jk">
              <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-4612" data-mp="2axxge1r6hqkt" />
              <input type="text" placeholder="Rechercher un permis..." className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-4613" data-mp="25yu8m9jtd695" />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-50 transition-colors" data-id="element-4614" data-mp="1luvb56dwcfgg">
              <FilterIcon className="w-4 h-4" data-id="element-4615" data-mp="1vtksmy1vrxw6" />
              Filtrer
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap" data-id="element-4616" data-mp="20t79dhgx3w9q">
              <PlusIcon className="w-4 h-4" data-id="element-4617" data-mp="1840cemx43eql" />
              Nouveau permis
            </button>
          </div>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && <div className="h-[400px] w-full border-b border-slate-200" data-id="element-4618" data-mp="1hu314ax7a6j4">
          <MapContainer center={[45.4499, -73.4653]} zoom={13} scrollWheelZoom={false} style={{
        height: '100%',
        width: '100%'
      }} data-id="element-4619" data-mp="1hve78he4bqde">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-4620" data-mp="23k8ul9kj78nx" />
            {MAP_MARKERS.map(m => <Marker key={m.permitId} position={m.position} icon={permitMarkerIcon} data-id="element-4621" data-mp="2dj7ua2nbjtys">
                <Popup data-id="element-4622" data-mp="2mj34a4551ix8">
                  <div className="text-sm" data-id="element-4623" data-mp="2mib9uwna1r8j">
                    <div className="font-semibold text-slate-900" data-id="element-4624" data-mp="2mh07905kipj0">
                      {m.permitId} — {m.type}
                    </div>
                    <div className="text-slate-500" data-id="element-4625" data-mp="2w9fp7xgv7e3w">{m.category}</div>
                  </div>
                </Popup>
              </Marker>)}
          </MapContainer>
        </div>}

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-4626" data-mp="366ak86dxiwdf">
        <table className="w-full text-left border-collapse" data-id="element-4627" data-mp="364zj6ebvc2q0">
          <thead data-id="element-4628" data-mp="31921a3kyiob4">
            <tr className="border-b border-slate-200 bg-white" data-id="element-4629" data-mp="319k9lykw8sze">
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4630" data-mp="3ktj3luhe680h">
                <span className="inline-flex items-center gap-1" data-id="element-4631" data-mp="3ovw5g7t0plrz">
                  Type de permis{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4632" data-mp="3awed5igijqdv" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4633" data-mp="3ftejluh9yv5i">
                <span className="inline-flex items-center gap-1" data-id="element-4634" data-mp="31509cyhj44ex">
                  N° Permis{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4635" data-mp="35xblr0tghiod" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4636" data-mp="2s6ydexcnk1yi">
                Emplacement
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4637" data-mp="2i77jaws07oyy">
                <span className="inline-flex items-center gap-1" data-id="element-4638" data-mp="2n5r3osqy8sp7">
                  Date de dépôt{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4639" data-mp="3e6pqw8eqz8e8" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4640" data-mp="3j5asab5qjy49">
                <span className="inline-flex items-center gap-1" data-id="element-4641" data-mp="34i7sfxtxxddr">
                  Statut{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4642" data-mp="39fr7v12djw7a" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4643" data-mp="2vfyo5pp9mg8t">
                Demandeur
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4644" data-mp="30di20oi1wr08">
                Valeur
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-4645" data-mp="2qhx3j0c6y8v8">
            {MOCK_PERMITS.map(permit => <tr key={permit.id} onClick={() => setSelectedPermit(permit)} className="hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-4646" data-mp="js0b46fzeh4p">
                <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap" data-id="element-4647" data-mp="298m8elyt13uf">
                  <div className="flex items-center gap-3" data-id="element-4648" data-mp="24aug3qu8zfpp">
                    <div className="w-8 h-8 rounded bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-4649" data-mp="2iynvpbjc2i8a">
                      <FileCheckIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-4650" data-mp="2e5ulw990cjpc" />
                    </div>
                    <div data-id="element-4651" data-mp="2rx9biyxf7j5d">
                      <div className="font-medium text-slate-900" data-id="element-4652" data-mp="2s0oqm1r3gs6s">
                        {permit.type}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-4653" data-mp="31x9vmn3463am">
                        {permit.category}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-slate-700 whitespace-nowrap" data-id="element-4654" data-mp="17i7af1l3l7di">
                  {permit.permitId}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-4655" data-mp="12m0g6622dkbe">
                  <div className="flex items-center gap-1.5" data-id="element-4656" data-mp="32nzxtzghf3lh">
                    <MapPinIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4657" data-mp="37kgmqvvi9lzm" />
                    {permit.location.split(',')[0]}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-4658" data-mp="3glwx53mblo3u">
                  {permit.date}
                </td>
                <td className="px-4 py-3 whitespace-nowrap" data-id="element-4659" data-mp="3ljyffmbx9je9">
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(permit.status)}`} data-id="element-4660" data-mp="3qk49ta9ktk01">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot(permit.status)}`} data-id="element-4661" data-mp="3vd6wqzjulv79"></div>
                    {permit.status}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-4662" data-mp="d4oggn3bwuap">
                  <div className="flex items-center gap-2" data-id="element-4663" data-mp="n94vxfcwc9dz">
                    <div className={`w-6 h-6 rounded-full ${permit.requester.color} text-white flex items-center justify-center text-[10px] font-bold`} data-id="element-4664" data-mp="napmyv005a73">
                      {permit.requester.initials}
                    </div>
                    {permit.requester.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-slate-700 whitespace-nowrap" data-id="element-4665" data-mp="1605uw3e0o4xj">
                  {permit.value}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-6 text-sm text-slate-500" data-id="element-4666" data-mp="w7q9sx7fb08f">
        <div className="flex items-center gap-2" data-id="element-4667" data-mp="s6er7bmyvf9q">
          Éléments par page
          <select className="border-b border-slate-300 pb-1 focus:outline-none bg-transparent font-medium text-slate-700" data-id="element-4668" data-mp="1orf40bjvkrol">
            <option data-id="element-4669" data-mp="14hdvsvp4keza">10</option>
            <option data-id="element-4670" data-mp="14h4fy0xsno7b">20</option>
            <option data-id="element-4671" data-mp="14ft2f9ejr230">50</option>
          </select>
        </div>
        <div data-id="element-4672" data-mp="19h0azaoik5aq">1 - 10 / 142</div>
        <div className="flex items-center gap-4" data-id="element-4673" data-mp="19ha4374uuskn">
          <button className="hover:text-slate-900" data-id="element-4674" data-mp="vgz85poounsm">
            <ChevronLeftIcon className="w-4 h-4" data-id="element-4675" data-mp="viadvv1p25md" />
          </button>
          <button className="hover:text-slate-900" data-id="element-4676" data-mp="zhj6uxowtzgv">
            <ChevronRightIcon className="w-4 h-4" data-id="element-4677" data-mp="zkds2rfqzobc" />
          </button>
        </div>
      </div>
    </div>;
}